import {
  type VendorListItem,
  type VendorListResponse,
  VendorListResponse as VendorListResponseSchema,
  isVendorComplete,
} from '@uit/shared/schemas/vendor';
import {
  type QbBulkLinkResponse,
  type QbLinkStatusResponse,
  QbLinkStatusResponse as QbLinkStatusResponseSchema,
} from '@uit/shared/schemas/quickbooks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { type LinkScope, LinkReviewDialog } from '../components/quickbooks/LinkReviewDialog.js';
import { CreateVendorDialog } from '../components/vendors/CreateVendorDialog.js';
import { useApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import s from './VendorsListPage.module.scss';

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const COMPANY_LABELS: Record<string, string> = {
  LLC: 'LLC',
  C_CORP: 'C-Corp',
  S_CORP: 'S-Corp',
};

function buildUrl(
  q: string,
  state: string,
  referrersOnly: boolean,
  notLinkedOnly: boolean,
  page: number,
): string {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (state.trim()) params.set('state', state.trim());
  if (referrersOnly) params.set('referrer', 'true');
  if (notLinkedOnly) params.set('qb_linked', 'false');
  params.set('page', String(page));
  return `/api/vendors?${params.toString()}`;
}

/**
 * Vendors list (T-199). Until now a vendor was reachable only by drilling down
 * from a resource row; this is the browsable index, and the home of the manual
 * New Vendor create that lets a vendor exist before anyone is onboarded under
 * it. Creating is admin-only (server-enforced); viewing follows the same
 * requireAuth as the vendor detail page, so onboarding_only can still browse.
 */
export function VendorsListPage() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [state, setState] = useState('');
  const [referrersOnly, setReferrersOnly] = useState(false);
  const [notLinkedOnly, setNotLinkedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<VendorListItem | null>(null);
  // Bulk / per-row "Link to QuickBooks" (T-205). `linkOpen` + `linkScope` drive the dialog;
  // linkScope null means "all unlinked".
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkScope, setLinkScope] = useState<LinkScope | null>(null);
  const [linked, setLinked] = useState<QbBulkLinkResponse | null>(null);

  const { admin } = useAuth();
  const canCreate = admin?.role === 'admin';

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, state, referrersOnly, notLinkedOnly]);

  const { data, error, loading, refetch } = useApi<VendorListResponse>(
    buildUrl(debouncedQ, state, referrersOnly, notLinkedOnly, page),
    VendorListResponseSchema,
  );
  const { data: linkStatus, refetch: refetchLinkStatus } = useApi<QbLinkStatusResponse>(
    canCreate ? '/api/quickbooks/link-status' : null,
    QbLinkStatusResponseSchema,
  );
  const canLink = canCreate && !!linkStatus?.connected;
  const unlinkedLeft = linkStatus ? linkStatus.unlinked_vendors + linkStatus.unlinked_services : 0;

  function openLink(scope: LinkScope | null): void {
    setLinkScope(scope);
    setLinkOpen(true);
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const filtered = !!debouncedQ || !!state || referrersOnly || notLinkedOnly;

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1 className={s.title}>Vendors</h1>
          <p className={s.subtitle}>
            The businesses ultimumIT contracts with, and the resources under each.
          </p>
        </div>
        {canCreate && (
          <button type="button" className={s.newButton} onClick={() => setCreateOpen(true)}>
            + New vendor
          </button>
        )}
      </div>

      {created && (
        <div className={s.createdBanner} role="status">
          <span>
            Created <strong>{created.business_name}</strong>. It can now be selected wherever a
            vendor is chosen — no onboarding needed.
          </span>
          <button
            type="button"
            className={s.createdBannerX}
            onClick={() => setCreated(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {canLink && unlinkedLeft > 0 && (
        <div className={s.linkBanner} role="status">
          <span>
            <strong>{plural(linkStatus!.unlinked_vendors, 'vendor')}</strong> and{' '}
            <strong>{plural(linkStatus!.unlinked_services, 'service')}</strong> aren&apos;t linked to
            QuickBooks yet — their bills would be <strong>held</strong> on the next push.
          </span>
          <button type="button" className={s.newButton} onClick={() => openLink(null)}>
            Link all to QuickBooks
          </button>
        </div>
      )}

      {linked && (
        <div className={s.createdBanner} role="status">
          <span>
            Linked <strong>{plural(linked.linked_vendors, 'vendor')}</strong> and{' '}
            <strong>{plural(linked.linked_services, 'service')}</strong> to QuickBooks.
            {linked.skipped.length > 0 &&
              ` ${linked.skipped.length} skipped — ${[...new Set(linked.skipped.map((x) => x.reason))].join('; ')}.`}
            {unlinkedLeft > 0 && (
              <>
                {' '}
                <strong>{unlinkedLeft}</strong> still need attention —{' '}
                <button type="button" className={s.inlineLink} onClick={() => setNotLinkedOnly(true)}>
                  show them
                </button>
                .
              </>
            )}
          </span>
          <button
            type="button"
            className={s.createdBannerX}
            onClick={() => setLinked(null)}
            aria-label="Dismiss link summary"
          >
            ×
          </button>
        </div>
      )}

      <div className={s.filters}>
        <input
          type="search"
          className={s.search}
          placeholder="Search vendor, city, or a resource's name…"
          aria-label="Search vendors"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <input
          type="text"
          className={s.filterState}
          placeholder="State"
          aria-label="Filter by state"
          maxLength={2}
          value={state}
          onChange={(e) => setState(e.target.value.toUpperCase())}
        />
        <label className={s.toggleReferrers}>
          <input
            type="checkbox"
            checked={referrersOnly}
            onChange={(e) => setReferrersOnly(e.target.checked)}
          />
          Referrers only
        </label>
        <label className={s.toggleReferrers}>
          <input
            type="checkbox"
            checked={notLinkedOnly}
            onChange={(e) => setNotLinkedOnly(e.target.checked)}
          />
          Not linked to QB
        </label>
      </div>

      {error && <div className={s.error}>Failed to load vendors: {error.message}</div>}
      {!error && loading && !data && <div className={s.loading}>Loading vendors…</div>}

      {data && data.items.length === 0 && (
        <div className={s.empty}>
          {notLinkedOnly && !debouncedQ && !state && !referrersOnly
            ? 'Every vendor is linked to QuickBooks.'
            : filtered
            ? 'No vendors match those filters.'
            : 'No vendors yet. They appear here once a resource is onboarded, or add one directly.'}
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <table className={s.table}>
            <thead>
              <tr>
                <th scope="col">Vendor</th>
                <th scope="col">Type</th>
                <th scope="col">Location</th>
                <th scope="col">Resources</th>
                <th scope="col">QuickBooks</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((v) => {
                const location = [v.business_address_city, v.business_address_state]
                  .filter(Boolean)
                  .join(', ');
                return (
                  <tr key={v.id}>
                    <td>
                      <Link to={`/vendors/${v.id}`} className={s.nameLink}>
                        {v.business_name}
                      </Link>
                      {v.is_referrer && <span className={s.badgeReferrer}>Referrer</span>}
                      {!isVendorComplete(v) && <span className={s.incomplete}>Incomplete</span>}
                    </td>
                    <td>
                      {v.company_type ? (
                        (COMPANY_LABELS[v.company_type] ?? v.company_type)
                      ) : (
                        <span className={s.muted}>—</span>
                      )}
                    </td>
                    <td>{location || <span className={s.muted}>—</span>}</td>
                    <td>{v.resource_count}</td>
                    <td>
                      {v.qb_linked ? (
                        <span className={s.qbLinked}>Linked</span>
                      ) : (
                        <>
                          <span className={s.qbUnlinked}>Not linked</span>
                          {canLink && (
                            <button
                              type="button"
                              className={s.rowLinkBtn}
                              aria-label={`Link ${v.business_name} to QuickBooks`}
                              onClick={() =>
                                openLink({ vendorId: v.id, vendorName: v.business_name })
                              }
                            >
                              Link…
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className={s.pagination}>
            <button
              type="button"
              className={s.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className={s.pageInfo}>
              {`Page ${data.page} of ${totalPages} · ${data.total} total`}
            </span>
            <button
              type="button"
              className={s.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {canCreate && (
        <LinkReviewDialog
          open={linkOpen}
          onOpenChange={setLinkOpen}
          scope={linkScope}
          onApplied={(result) => {
            setLinked(result);
            void refetch();
            void refetchLinkStatus();
          }}
        />
      )}

      {canCreate && (
        <CreateVendorDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(vendor) => {
            setCreated(vendor);
            void refetch();
          }}
        />
      )}
    </div>
  );
}
